import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash';
import { FormattedMessage, intlShape } from 'react-intl';
import classNames from 'classnames';
import Breadcrumb from '../elements-breadcrum/breadcrumb';
import Button from '../elements-buttons/Button';
import { BookingSummaryListing } from '../elements-summaries/common/items/BookingSummaryListing';
import EventFAQBookingSummary from '../elements-summaries/common/items/EventFAQBookingSummary';
import InquiryForm from './InquiryForm/InquiryForm';
import { InquiryHostBanner } from './InquiryHostBanner/InquiryHostBanner';
import { InquiryFeeCalc } from './InquiryFeeCalc/InquiryFeeCalc';
import {
  useInquiry,
  useInquiryBreadcrumbs
} from './utils';
import { useWindowSize } from '../shared-components/utils/hooks/use-window-size';
import EventFAQModal from './EventFAQModal';
import { New } from '../shared-components/icons';
import { useStatsigContext } from '../elements-statsig-provider/utils';

export const InquiryMain = (props) => {
  const {
    dispatch,
    listing = {},
    flow = {},
    handleSubmitForm,
    router,
    profile,
    activity,
    numberOfGuests,
    alcoholConsumed,
    message,
    organization,
    schedule,
    profession,
    accessToken,
    faqData,
    intl
  } = props;

  const breadcrumbProps = useInquiryBreadcrumbs(listing._id, flow, dispatch);

  const [showModal, setShowModal] = useState(false);

  const windowDims = useWindowSize();

  const {
    initialized: statsigInitialized,
    featureGates
  } = useStatsigContext();

  const {
    quote,
    errors: quoteErrors,
    isQuoteLoading,
    getFeeCalc,
    isInquiryLoading,
    sendInquiryRequest,
    isAttendeesPricing
  } = useInquiry(
    accessToken,
    profile,
    listing,
    {
      activity,
      numberOfGuests,
      alcoholConsumed,
      message,
      organization,
      schedule,
      profession
    },
    intl
  );

  useEffect(() => {
    if (statsigInitialized) {
      const listingId = get(listing, '_id');
      analyticsIntegrations.trackEvent('inquiry_page_loaded', { listing_id: listingId });
    }
  }, [statsigInitialized]);

  useEffect(() => {
    const sortOrder = router.location?.query?.sort_order;
    const msgObj = {
      guest_has_organization: get(profile, 'data.organization', ''),
      guest_has_profession: get(profile, 'data.profession', ''),
      sort_rank: sortOrder
    };
    analyticsIntegrations.messageTrack(
      'inquiry form viewed',
      listing,
      {},
      msgObj
    );
  }, []);

  useEffect(()=> {
    if (showModal && analyticsIntegrations) {
      const listingId = get(listing, '_id');
      const userId = get(profile, 'SSOid', '');
      analyticsIntegrations.trackEvent('event FAQ modal opened', {
        user_id: userId,
        listing_id: listingId
      });
    }
  }, [showModal]);

  const {
    ownerAvatarId = '',
    ownerFirstName = '',
    ownerLastName = '',
    host_responsiveness: responsiveness = {}
  } = listing;

  const listingImageId = get(listing, 'images[0].external_id', '');
  const currency = get(listing, 'prices[0].currency', '');
  const displayEventFAQ = featureGates?.event_faq_listing_page?.() && !isEmpty(faqData);
  return (
    <>
      <div className='ps-container-lg-margin ps-container-fixed inquiry-request-body'>
        <div className='ps-row'>
          <div className='col-xs-12'>
            <Breadcrumb {...breadcrumbProps} />
            <div className='inquiry-page-margins'>
              <InquiryHostBanner
                firstName={ownerFirstName}
                lastName={ownerLastName}
                ownerAvatar={ownerAvatarId}
                listingImageId={listingImageId}
                responsiveness={responsiveness}
                intl={intl}
              />
            </div>
            <hr className='w-100 no-margin d-xs-none d-lg-block' />
            <div className="d-xs-block d-lg-none">
              {displayEventFAQ && <div
                className="faq-label text-cream-dark bg-cream margin-lg-bottom text-small flex middle rounded-corners"
                style={{ padding: '8px 12px' }}
              >
                <div className="flex middle">
                  <New height={24} width={24} style={{ marginRight: '12px' }} />
                </div>
                <FormattedMessage
                  id='inquiry.event_faq.info_bar.text'
                  defaultMessage='Questions about parking, food rules, alcohol usage, and more? {eventFAQLink}'
                  values={{
                    eventFAQLink: (
                      <a
                        style={{ color: '#886A2F', fontWeight: 'bold' }}
                        onClick={() => setShowModal(true)}
                        className="text-bold underline"
                      >
                        <FormattedMessage
                          id="inquiry.event_faq_info_bar.link_label"
                          defaultMessage="See FAQ"
                        />
                      </a>
                    )
                  }}
                />
              </div>}
            </div>
            <div className="ps-row-nested">
              <div className='form-banner inquiry-page-margins col-sm-7'>
                <h5 className='h5 form-header no-margin margin-sm-bottom'>
                  <FormattedMessage
                    id='inquiry_booking.inquiry_main.form_header'
                    defaultMessage='Introduce yourself and your activity'
                  />
                </h5>
                <p className='text-base text-gray-dark form-subheader no-margin'>
                  <FormattedMessage
                    id='inquiry_booking.inquiry_main.form_subheader'
                    defaultMessage='Share {link} with the host'
                    values={{
                      link: (
                        <span className='text-primary'>
                          <FormattedMessage
                            id='inquiry_booking.inquiry_main.form_subheader_link'
                            defaultMessage='*required details'
                          />
                        </span>
                      )
                    }}
                  />
                </p>
              </div>
              {displayEventFAQ && <div
                className="d-xs-none d-lg-block col-xs-12 col-lg-5 inquiry-page-margins"
                style={{ marginBottom: '48px' }}
              >
                <h5 className='h5 form-header no-margin margin-sm-bottom'>
                  <FormattedMessage
                    id="inquiry_booking.booking_summar_listing.event_faq_header"
                    defaultMessage="Event FAQ"
                  />
                </h5>
                <a className='underline text-gray-dark' onClick={() => setShowModal(true)}>
                  <FormattedMessage
                    id="inquiry_booking.booking_summar_listing.event_faq_cta"
                    defaultMessage="View all"
                  /></a>
              </div>}
            </div>
          </div>
        </div>
      </div>

      <div
        className={classNames({
          'ps-container-lg-margin ps-container-fixed': windowDims.width >= 768
        })}
      >
        <div
          className={classNames({
            'ps-row': windowDims.width >= 768
          })}
        >
          <div
            className={classNames({
              'col-xs-12 col-lg-7': windowDims.width >= 768
            })}
          >
            <InquiryForm
              {...props}
              getFeeCalc={getFeeCalc}
              sendInquiryRequest={sendInquiryRequest}
              quoteErrors={quoteErrors}
              useGrid={windowDims.width < 768}
              isAttendeesPricing={isAttendeesPricing}
            />
          </div>

          <div className='d-xs-none d-lg-block col-xs-12 col-lg-5' style={{ marginBottom: '42px' }}>
            <div className='border padding-xl-top padding-xl-bottom padding-xl-left padding-xl-right'>
              <BookingSummaryListing listing={listing} intl={intl} />
              {displayEventFAQ && <EventFAQBookingSummary faqData={faqData} openModal={() => setShowModal(true)} />}
            </div>
          </div>
        </div>
      </div>

      <div className='ps-container-lg-margin ps-container-fixed inquiry-page-footer'>
        <div className='ps-row' style={{ height: '100%' }}>
          <div className="flex middle start col-sm-12 col-md-8">
            <InquiryFeeCalc
              quote={quote}
              isLoading={isQuoteLoading}
              listing={listing}
              profile={profile}
              currency={currency}
              inputs={{
                schedule,
                attendees: numberOfGuests
              }}
              intl={intl}
            />
          </div>
          <div className="flex middle end col-sm-12 col-md-4">
            <Button
              type='submit'
              category='primary'
              onClick={handleSubmitForm}
              isLoading={isInquiryLoading}
              disabled={isInquiryLoading}
              className='btn-primary submit-form-btn'
            >
              <FormattedMessage
                id='inquiry_booking.inquiry_main.message_host'
                defaultMessage='Message Host'
              />
            </Button>
          </div>
        </div>
      </div>
      {displayEventFAQ && <EventFAQModal
        addCloseIcon
        modalClass="EventFAQModal"
        faqData={faqData}
        showModal={showModal}
        toggleModal={() => setShowModal(!showModal)}
        listingId={props?.listing?._id}
        accessToken={accessToken}
      />}
    </>
  );
};

InquiryMain.propTypes = {
  dispatch: PropTypes.func,
  listing: PropTypes.object,
  flow: PropTypes.object,
  handleSubmitForm: PropTypes.func,
  inquiryBooking: PropTypes.object,
  router: PropTypes.object,
  profile: PropTypes.object,
  activity: PropTypes.string,
  numberOfGuests: PropTypes.number,
  alcoholConsumed: PropTypes.bool,
  message: PropTypes.string,
  organization: PropTypes.string,
  schedule: PropTypes.object,
  profession: PropTypes.string,
  accessToken: PropTypes.string,
  statsigInitialized: PropTypes.bool,
  faqData: PropTypes.object,
  intl: intlShape
};
